import {
  supabase,
} from '@/lib/supabase';

export type CallsLifecycleMode =
  | 'voice'
  | 'video';

export type CreateCallSessionInput = Readonly<{
  callerEmail: string;
  receiverEmail: string;
  callerName: string;
  receiverName: string;
  roomName: string;
  mode: CallsLifecycleMode;
}>;

export type CallSessionSelector = Readonly<{
  callSessionId?: string | null;
  callerEmail?: string | null;
  receiverEmail?: string | null;
  roomName?: string | null;
}>;

export type CallsLifecycleMutationResult =
  Readonly<{
    ok: boolean;
    callSessionId: string | null;
    previousStatus: string | null;
    nextStatus: string;
    changed: boolean;
    error: string | null;
  }>;

const normalizeEmail = (
  value: string | null | undefined,
): string =>
  (value ?? '').trim().toLowerCase();

const failure = (
  nextStatus: string,
  error: unknown,
): CallsLifecycleMutationResult =>
  Object.freeze({
    ok: false,
    callSessionId: null,
    previousStatus: null,
    nextStatus,
    changed: false,
    error:
      error instanceof Error
        ? error.message
        : String(error),
  });

export async function createRingingCallSession(
  input: CreateCallSessionInput,
): Promise<CallsLifecycleMutationResult> {
  try {
    const callerEmail =
      normalizeEmail(input.callerEmail);

    const receiverEmail =
      normalizeEmail(input.receiverEmail);

    if (
      !callerEmail ||
      !receiverEmail ||
      !input.roomName
    ) {
      return failure(
        'ringing',
        'Missing caller, receiver, or room authority.',
      );
    }

    const {
      data,
      error,
    } = await supabase
      .from('call_sessions')
      .insert({
        caller_email: callerEmail,
        receiver_email: receiverEmail,
        caller_name:
          input.callerName || callerEmail,
        receiver_name:
          input.receiverName || receiverEmail,
        room_name: input.roomName,
        mode: input.mode,
        status: 'ringing',
      })
      .select('id,status')
      .single();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId: data?.id ?? null,
      previousStatus: null,
      nextStatus: 'ringing',
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure('ringing', error);
  }
}

export async function acceptRingingCallSession(
  selector: CallSessionSelector,
): Promise<CallsLifecycleMutationResult> {
  try {
    const receiverEmail =
      normalizeEmail(selector.receiverEmail);

    if (!receiverEmail) {
      return failure(
        'accepted',
        'Receiver identity is required.',
      );
    }

    let query = supabase
      .from('call_sessions')
      .update({
        status: 'accepted',
        answered_at:
          new Date().toISOString(),
      })
      .eq(
        'receiver_email',
        receiverEmail,
      )
      .eq('status', 'ringing');

    if (selector.callSessionId) {
      query = query.eq(
        'id',
        selector.callSessionId,
      );
    } else if (selector.callerEmail) {
      query = query.eq(
        'caller_email',
        normalizeEmail(
          selector.callerEmail,
        ),
      );
    } else {
      return failure(
        'accepted',
        'Session ID or caller identity is required.',
      );
    }

    const {
      data,
      error,
    } = await query
      .select('id,status')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId:
        data?.id ??
        selector.callSessionId ??
        null,
      previousStatus:
        data ? 'ringing' : null,
      nextStatus: 'accepted',
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure('accepted', error);
  }
}

export async function markRingingCallMissed(
  selector: CallSessionSelector,
): Promise<CallsLifecycleMutationResult> {
  try {
    let query = supabase
      .from('call_sessions')
      .update({
        status: 'missed',
        ended_at:
          new Date().toISOString(),
      })
      .eq('status', 'ringing');

    if (selector.callSessionId) {
      query = query.eq(
        'id',
        selector.callSessionId,
      );
    } else {
      const callerEmail =
        normalizeEmail(
          selector.callerEmail,
        );

      const receiverEmail =
        normalizeEmail(
          selector.receiverEmail,
        );

      if (
        !callerEmail ||
        !receiverEmail ||
        !selector.roomName
      ) {
        return failure(
          'missed',
          'Session ID or complete call identity is required.',
        );
      }

      query = query
        .eq(
          'caller_email',
          callerEmail,
        )
        .eq(
          'receiver_email',
          receiverEmail,
        )
        .eq(
          'room_name',
          selector.roomName,
        );
    }

    const {
      data,
      error,
    } = await query
      .select('id,status')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId:
        data?.id ??
        selector.callSessionId ??
        null,
      previousStatus:
        data ? 'ringing' : null,
      nextStatus: 'missed',
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure('missed', error);
  }
}

export async function endCallSession(
  selector: CallSessionSelector,
): Promise<CallsLifecycleMutationResult> {
  try {
    const activeStatuses = [
      'ringing',
      'accepted',
      'connecting',
      'connected',
    ];

    let query = supabase
      .from('call_sessions')
      .update({
        status: 'ended',
        ended_at:
          new Date().toISOString(),
      })
      .in(
        'status',
        activeStatuses,
      );

    if (selector.callSessionId) {
      query = query.eq(
        'id',
        selector.callSessionId,
      );
    } else {
      const callerEmail =
        normalizeEmail(
          selector.callerEmail,
        );

      const receiverEmail =
        normalizeEmail(
          selector.receiverEmail,
        );

      if (
        !callerEmail ||
        !receiverEmail ||
        !selector.roomName
      ) {
        return failure(
          'ended',
          'Session ID or complete call identity is required.',
        );
      }

      query = query
        .eq(
          'room_name',
          selector.roomName,
        )
        .or(
          [
            `and(caller_email.eq.${callerEmail},receiver_email.eq.${receiverEmail})`,
            `and(caller_email.eq.${receiverEmail},receiver_email.eq.${callerEmail})`,
          ].join(','),
        );
    }

    const {
      data,
      error,
    } = await query
      .select('id,status')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId:
        data?.id ??
        selector.callSessionId ??
        null,
      previousStatus: null,
      nextStatus: 'ended',
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure('ended', error);
  }
}

export type CallsConnectionLifecycleState =
  | 'connecting'
  | 'connected'
  | 'ended';

export async function transitionCallSessionById(
  callSessionId: string,
  nextStatus: CallsConnectionLifecycleState,
): Promise<CallsLifecycleMutationResult> {
  try {
    if (!callSessionId) {
      return failure(
        nextStatus,
        'Call session identity is required.',
      );
    }

    const allowedCurrentStatuses = [
      'ringing',
      'accepted',
      'connecting',
      'connected',
    ];

    const update =
      nextStatus === 'ended'
        ? {
            status: 'ended',
            ended_at:
              new Date().toISOString(),
          }
        : {
            status: nextStatus,
          };

    const {
      data,
      error,
    } = await supabase
      .from('call_sessions')
      .update(update)
      .eq('id', callSessionId)
      .in(
        'status',
        allowedCurrentStatuses,
      )
      .select('id,status')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId:
        data?.id ??
        callSessionId,
      previousStatus: null,
      nextStatus,
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure(
      nextStatus,
      error,
    );
  }
}

export async function transitionCallSessionByRoom(
  roomName: string,
  nextStatus: CallsConnectionLifecycleState,
): Promise<CallsLifecycleMutationResult> {
  try {
    if (!roomName) {
      return failure(
        nextStatus,
        'Room identity is required.',
      );
    }

    const allowedCurrentStatuses = [
      'ringing',
      'accepted',
      'connecting',
      'connected',
    ];

    const {
      data: latestSession,
      error: lookupError,
    } = await supabase
      .from('call_sessions')
      .select('id,status')
      .eq('room_name', roomName)
      .in(
        'status',
        allowedCurrentStatuses,
      )
      .order(
        'created_at',
        { ascending: false },
      )
      .limit(1)
      .maybeSingle();

    if (lookupError) {
      throw lookupError;
    }

    if (!latestSession?.id) {
      return Object.freeze({
        ok: true,
        callSessionId: null,
        previousStatus: null,
        nextStatus,
        changed: false,
        error: null,
      });
    }

    const update =
      nextStatus === 'ended'
        ? {
            status: 'ended',
            ended_at:
              new Date().toISOString(),
          }
        : {
            status: nextStatus,
          };

    const {
      data,
      error,
    } = await supabase
      .from('call_sessions')
      .update(update)
      .eq(
        'id',
        latestSession.id,
      )
      .eq(
        'status',
        latestSession.status,
      )
      .select('id,status')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return Object.freeze({
      ok: true,
      callSessionId:
        data?.id ??
        latestSession.id,
      previousStatus:
        latestSession.status,
      nextStatus,
      changed: Boolean(data?.id),
      error: null,
    });
  } catch (error) {
    return failure(
      nextStatus,
      error,
    );
  }
}
